import React, { useCallback, useEffect, useState } from 'react';
import {
  HiPlay, HiPause, HiCog, HiPlus, HiTrash, HiRefresh,
} from 'react-icons/hi';
import apiClient from '../../api/client';
import { useNotification } from '../../context/NotificationContext';
import ModalOverlay from '../../components/ModalOverlay';
import MpDailyChart, { KpiGrid, mpFmt as fmt } from '../../components/MpDailyChart';
import MarketplaceStrategyModal from '../../components/MarketplaceStrategyModal';
import { formatMarketplaceStrategy, strategyFormToPayload } from '../../utils/marketplaceStrategies';

export default function OzonCampaignCabinet({ clientId }) {
  const { notify, confirm } = useNotification();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settingsCamp, setSettingsCamp] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBudget, setNewBudget] = useState(3000);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [manageRes, summaryRes] = await Promise.all([
        apiClient.get('/ozon/campaigns/manage', { params: { client_id: clientId } }),
        apiClient.get('/ozon/campaigns/summary', { params: { client_id: clientId } }),
      ]);
      setItems(manageRes.data?.items || []);
      setSummary(summaryRes.data);
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка загрузки', 'error');
    } finally {
      setLoading(false);
    }
  }, [clientId, notify]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async (values) => {
    try {
      const payload = strategyFormToPayload(values, 'ozon');
      await apiClient.patch(`/ozon/campaigns/${settingsCamp.campaign_id}`, {
        client_id: clientId,
        ...payload,
        bidder_enabled: values.bidder_enabled ? 1 : 0,
        automation_enabled: values.automation_enabled ? 1 : 0,
      });
      notify('Настройки сохранены', 'success');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка сохранения', 'error');
      throw e;
    }
  };

  const toggle = async (camp, activate) => {
    try {
      const url = activate
        ? `/ozon/campaigns/${camp.campaign_id}/activate`
        : `/ozon/campaigns/${camp.campaign_id}/deactivate`;
      await apiClient.post(url, { client_id: clientId });
      notify(activate ? 'Кампания запущена' : 'Кампания остановлена', 'success');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка', 'error');
    }
  };

  const deleteCamp = async (camp) => {
    const ok = await confirm({ message: `Удалить «${camp.title}»?`, danger: true, confirmText: 'Удалить' });
    if (!ok) return;
    try {
      await apiClient.delete(`/ozon/campaigns/${camp.campaign_id}`, { params: { client_id: clientId } });
      notify('Удалено', 'success');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка', 'error');
    }
  };

  const createCamp = async () => {
    try {
      await apiClient.post('/ozon/campaigns', {
        client_id: clientId,
        title: newTitle,
        daily_budget: newBudget,
      });
      notify('Кампания создана', 'success');
      setShowCreate(false);
      setNewTitle('');
      load();
    } catch (e) {
      notify(e.response?.data?.message || 'Ошибка', 'error');
    }
  };

  if (loading) return <p className="text-[var(--wb-text-muted)]">Загрузка кампаний Ozon…</p>;

  const totals = summary?.totals || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Реклама Ozon</h2>
          <p className="text-sm text-[var(--wb-text-muted)]">
            Кампании, бюджеты, биддер, остатки — аналог вкладки WB
          </p>
        </div>
        <div className="flex gap-2">
          <button type="button" className="wb-btn-secondary" onClick={load}>
            <HiRefresh className="w-4 h-4" />
          </button>
          <button type="button" className="wb-btn" onClick={() => setShowCreate(true)}>
            <HiPlus className="w-4 h-4" /> Создать
          </button>
        </div>
      </div>

      <KpiGrid items={[
        { label: 'Расход', value: `${fmt(totals.spend)} ₽` },
        { label: 'Выручка', value: `${fmt(totals.revenue)} ₽` },
        { label: 'Показы', value: fmt(totals.views) },
        { label: 'Клики', value: fmt(totals.clicks) },
        { label: 'CTR', value: `${fmt(totals.ctr, 1)}%` },
        { label: 'CPC', value: totals.cpc != null ? `${fmt(totals.cpc, 2)} ₽` : '—' },
        { label: 'Заказы', value: fmt(totals.orders) },
        { label: 'ДРР', value: `${fmt(totals.drr, 1)}%` },
      ]} />

      <MpDailyChart
        data={summary?.daily || []}
        lines={[
          { key: 'spend', name: 'Расход, ₽', color: '#3b82f6' },
          { key: 'revenue', name: 'Выручка, ₽', color: '#f97316' },
          { key: 'views', name: 'Показы', color: '#4f46e5' },
          { key: 'clicks', name: 'Клики', color: '#10b981' },
          { key: 'ctr', name: 'CTR, %', color: '#14b8a6', yAxisId: 'right' },
          { key: 'cpc', name: 'CPC, ₽', color: '#8b5cf6', yAxisId: 'right' },
        ]}
      />

      <div className="wb-card overflow-x-auto">
        <table className="wb-data-table">
          <thead>
            <tr>
              <th>Кампания</th>
              <th>Статус</th>
              <th className="wb-col-num">Бюджет/день</th>
              <th className="wb-col-num">Показы</th>
              <th className="wb-col-num">Клики</th>
              <th className="wb-col-num">CTR</th>
              <th className="wb-col-num">CPC</th>
              <th className="wb-col-num">Расход</th>
              <th className="wb-col-num">Выручка</th>
              <th className="wb-col-num">Заказы</th>
              <th className="wb-col-num">ДРР</th>
              <th className="wb-col-num">Остаток</th>
              <th>Стратегия</th>
              <th className="wb-col-actions">Действия</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.campaign_id}>
                <td>
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs text-[var(--wb-text-muted)]">{c.payment_type} · {c.campaign_id}</div>
                </td>
                <td>
                  <span className={c.is_running ? 'text-emerald-600' : 'text-[var(--wb-text-muted)]'}>
                    {c.is_running ? 'Активна' : 'Остановлена'}
                  </span>
                </td>
                <td className="wb-col-num">{fmt(c.daily_budget)} ₽</td>
                <td className="wb-col-num">{fmt(c.views)}</td>
                <td className="wb-col-num">{fmt(c.clicks)}</td>
                <td className="wb-col-num">{fmt(c.ctr, 1)}%</td>
                <td className="wb-col-num">{c.cpc != null ? `${fmt(c.cpc, 2)} ₽` : '—'}</td>
                <td className="wb-col-num">{fmt(c.spend)} ₽</td>
                <td className="wb-col-num">{fmt(c.revenue)} ₽</td>
                <td className="wb-col-num">{fmt(c.orders)}</td>
                <td className="wb-col-num">{fmt(c.drr, 1)}%</td>
                <td className="wb-col-num">
                  <span className={c.stock_available > 0 ? 'text-emerald-600' : 'text-red-600'}>
                    {fmt(c.stock_available)}
                  </span>
                </td>
                <td>
                  <div className="text-xs text-gray-600 max-w-[140px]">{formatMarketplaceStrategy(c, 'ozon')}</div>
                </td>
                <td className="wb-col-actions">
                  <div className="inline-flex items-center justify-center gap-1">
                    {c.is_running ? (
                      <button type="button" className="p-1.5 rounded hover:bg-[var(--wb-surface-muted)]" title="Остановить"
                        onClick={() => toggle(c, false)}><HiPause className="w-4 h-4" /></button>
                    ) : (
                      <button type="button" className="p-1.5 rounded hover:bg-[var(--wb-surface-muted)]" title="Запустить"
                        onClick={() => toggle(c, true)}><HiPlay className="w-4 h-4" /></button>
                    )}
                    <button type="button" className="p-1.5 rounded hover:bg-[var(--wb-surface-muted)]" title="Настройки"
                      onClick={() => setSettingsCamp(c)}><HiCog className="w-4 h-4" /></button>
                    <button type="button" className="p-1.5 rounded hover:bg-[var(--wb-danger-bg)] text-[var(--wb-danger)]" title="Удалить"
                      onClick={() => deleteCamp(c)}><HiTrash className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <MarketplaceStrategyModal
        open={Boolean(settingsCamp)}
        onClose={() => setSettingsCamp(null)}
        title={settingsCamp?.title}
        subtitle={`ID ${settingsCamp?.campaign_id} · ${settingsCamp?.payment_type || 'CPC'}`}
        marketplace="ozon"
        initialRow={settingsCamp}
        onSave={saveSettings}
        showStockThreshold
      />

      {showCreate && (
        <ModalOverlay onClose={() => setShowCreate(false)}>
          <div className="wb-card p-6 max-w-md w-full space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-semibold">Новая кампания Ozon</h3>
            <input className="wb-input w-full" placeholder="Название" value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)} />
            <input type="number" className="wb-input w-full" placeholder="Бюджет/день" value={newBudget}
              onChange={(e) => setNewBudget(Number(e.target.value))} />
            <button type="button" className="wb-btn" onClick={createCamp} disabled={!newTitle.trim()}>Создать</button>
          </div>
        </ModalOverlay>
      )}
    </div>
  );
}
